import React, { useEffect, useMemo, useState } from 'react';
import { Landmark, Plus, RefreshCw } from 'lucide-react';
import http from '../api/http';
import { useFiliale } from '../context/FilialeContext';
import BlueHeader from '../components/BlueHeader';
import { ALL_FILIALES_ID } from '../data/filiales';
import { Avatar, Field, IdChip, ListCard, Modal, RowActions, Table, inputCls, rowCls, tdCls } from '../components/ui';

const Banques = () => {
  const { filialeId } = useFiliale();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});

  const isAllFiliales = !filialeId || filialeId === 'all' || filialeId === ALL_FILIALES_ID;

  const fetchData = async () => {
    try {
      setLoading(true);
      let endpoint = '/api/banques';
      // Filiale spécifique : on envoie filiale_id. Sinon, consolidé (super_admin) ou filiale de l'utilisateur.
      if (!isAllFiliales) endpoint += `?filiale_id=${filialeId}`;
      const response = await http.get(endpoint);
      setData(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erreur:', error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [filialeId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data;
    return data.filter((i) => [i.libelle_banque, i.code].some((v) => (v || '').toLowerCase().includes(q)));
  }, [data, search]);

  const handleCreate = () => {
    if (isAllFiliales) {
      alert('Veuillez sélectionner une filiale spécifique dans le Périmètre avant de créer une banque.');
      return;
    }
    setEditingItem(null);
    setFormData({ filiale_id: parseInt(filialeId, 10) });
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({ ...item });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette banque ?')) return;
    try {
      await http.delete(`/api/banques/${id}`);
      fetchData();
    } catch (error) {
      alert('Erreur lors de la suppression: ' + (error.response?.data?.error || 'Erreur inconnue'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) await http.put(`/api/banques/${editingItem.idbanque}`, formData);
      else await http.post('/api/banques', formData);
      setShowModal(false);
      fetchData();
    } catch (error) {
      alert('Erreur: ' + (error.response?.data?.error || 'Erreur inconnue'));
    }
  };

  return (
    <div className="min-h-full bg-slate-50 dark:bg-slate-950">
      <BlueHeader
        title="Banques"
        subtitle="Gestion des banques"
        icon={Landmark}
        showBack={false}
        actions={[
          { label: 'Créer', icon: Plus, onClick: handleCreate, primary: true },
          { label: 'Actualiser', icon: RefreshCw, onClick: fetchData, primary: false },
        ]}
      />

      <div className="p-6">
        <ListCard
          count={filtered.length} singular="banque affichée" plural="banques affichées"
          search={search} onSearch={setSearch} placeholder="Rechercher par nom ou code"
          loading={loading} hasData={data.length > 0} icon={Landmark}
          emptyTitle="Aucune banque pour le moment" emptyText="Créez votre première banque pour commencer."
          onCreate={handleCreate} createLabel="Créer une banque" createIcon={Plus}
        >
          <Table heads={['Banque', 'Code', 'Actions']}>
            {filtered.map((item) => (
              <tr key={item.idbanque} className={rowCls}>
                <td className={tdCls}>
                  <div className="flex items-center gap-3">
                    <Avatar icon={Landmark} />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-slate-900 dark:text-white">{item.libelle_banque}</p>
                      <p className="mt-0.5"><IdChip>#{item.idbanque}</IdChip></p>
                    </div>
                  </div>
                </td>
                <td className={tdCls}>
                  {item.code ? <IdChip>{item.code}</IdChip> : <span className="text-sm text-slate-400">-</span>}
                </td>
                <td className={tdCls}>
                  <RowActions onEdit={() => handleEdit(item)} onDelete={() => handleDelete(item.idbanque)} />
                </td>
              </tr>
            ))}
          </Table>
        </ListCard>
      </div>

      {showModal && (
        <Modal
          title={editingItem ? 'Modifier la banque' : 'Créer une banque'}
          subtitle={editingItem ? editingItem.libelle_banque : 'Renseignez les informations de la banque'}
          onClose={() => setShowModal(false)}
          onSubmit={handleSubmit}
          confirmLabel={editingItem ? 'Enregistrer' : 'Créer'}
        >
          <div className="space-y-5 p-6">
            <Field label="Libellé">
              <input
                type="text"
                className={inputCls}
                value={formData.libelle_banque || ''}
                onChange={(e) => setFormData({ ...formData, libelle_banque: e.target.value })}
                required
              />
            </Field>
            <Field label="Code">
              <input
                type="text"
                className={inputCls}
                value={formData.code || ''}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                required
              />
            </Field>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Banques;